<template>
  <td :class="classes">
    <SfLink
      v-if="hasLink"
      class="sf-inline"
      :href="href"
      :target="linkTarget"
      data-testid="entries-table-cell-link"
      @click="onClick"
    >
      <i v-if="icon" :class="icon"></i>
      {{ text }}
    </SfLink>
    <template v-else>
      {{ text }}
    </template>
  </td>
</template>

<script setup lang="ts">
import type { TableEntry } from '@app/types'

import { computed } from 'vue'

import SfLink from '@app/shared/vue/components/sf-link.vue'

interface Props {
  entry: TableEntry
  header: string
}

const props = withDefaults(defineProps<Props>(), {})

const classes = computed(() => {
  return props.header === 'value' || hasJournalLink.value ? 'sf-alignr' : ''
})

const hasAdjustmentLink = computed(() => {
  return props.entry.link_to_adjustment && props.header === 'value'
})

const hasDocumentLink = computed(() => {
  return (
    props.header === 'document_reference' && !!props.entry.document_reference
  )
})

const hasJournalLink = computed(() => {
  return props.header === 'journal' && !!props.entry.journal_link
})

const hasLink = computed(() => {
  return (
    hasAdjustmentLink.value || hasDocumentLink.value || hasJournalLink.value
  )
})

const href = computed(() => {
  if (hasAdjustmentLink.value) {
    return props.entry.link_to_adjustment
  } else if (hasDocumentLink.value) {
    return window.Silverfin.Routes.document_path(
      window.Silverfin.currentFirmId,
      window.Silverfin.currentCompanyId,
      props.entry['document_reference'],
      { detached: 1 }
    )
  } else if (hasJournalLink.value) {
    return '#'
  } else {
    return ''
  }
})

const journalLink = computed(() => {
  return props.entry.journal_link
})

const icon = computed(() => {
  return hasDocumentLink.value ? 'i-documents-file-pdf' : ''
})

const linkTarget = computed(() => {
  return hasDocumentLink.value || hasAdjustmentLink.value ? '_blank' : '_self'
})

const text = computed(() => {
  if (hasAdjustmentLink.value) {
    return props.entry[props.header]
  } else if (hasDocumentLink.value) {
    return ''
  } else if (hasJournalLink.value) {
    return '#'
  } else {
    return props.entry[props.header]
  }
})

function onClick(e: Event) {
  if (hasJournalLink.value && journalLink.value) {
    e.preventDefault()
    e.stopPropagation()
    window.modalCitizen.openXhrModal(e, 'journal', journalLink.value, {})
  }
}
</script>
